/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import classnames from 'classnames';
import * as React from 'react';

import { IHeaderViewProps } from '@msdyn365-commerce-modules/header';
import { Collapse, Drawer, Module, Node } from '@msdyn365-commerce-modules/utilities';
import { AsyncResult } from '@msdyn365-commerce/core';
import { Customer } from '@msdyn365-commerce/retail-proxy';
import { IHeaderProps } from '../definition-extensions/header.ext.props.autogenerated';

import { getSourceIdStatusAsync } from './../../../actions/FlintFoxSourceCodeAction.g'

import Cookies from 'universal-cookie';
import { IFlintfoxSourceCodeStatus } from '../../../actions/FlintFoxSourceCodeEntities.g';

const headerView: React.FC<IHeaderViewProps & IHeaderProps<{}>> = props => {
    const {
        HeaderTag,
        HeaderContainer,
        HeaderTopBarContainer,
        Divider
    } = props;

    // ITS Customization - Embedded src code
    const [urlCode, setUrlCode] = React.useState<string>('');
    const urlQuery = props.context.request.query;
    const cookies = new Cookies();
    const cookieExpireDay = props.context.app.config.embeddedSourceCodeExpiryDuration;
    const cookieExpiryDate = new Date(Date.now() + cookieExpireDay * 24 * 60 * 60 * 1000);
    
    const getSourceCodeResponse = React.useCallback(async () => {
        try {
            return await getSourceIdStatusAsync(
                { callerContext: props.context.actionContext },
                urlCode
            ).catch(err => {
                console.log(err);
            })
        }
        catch (error) {
            console.log(error);
        }
    }, [urlCode]);

    React.useEffect(() => {
        if (urlQuery && urlQuery?.code !== undefined) {
            setUrlCode(urlQuery?.code);
        }
    }, []);

    React.useEffect(() => {
        if(urlCode && urlCode !== '') {
            getSourceCodeResponse().then((response: (void | IFlintfoxSourceCodeStatus)) => {
                if (cookies && urlCode && response && response.Result === 'Success') {
                    cookies.set('sourceCode', urlCode, { path: '/', expires: cookieExpiryDate });
                }
            })
        }
    }, [urlCode])

    // ITS Customization - Embedded src code end
    return (
        <Module {...HeaderTag}>
            <Node {...HeaderContainer}>
                <Node {...HeaderTopBarContainer}>
                    {/* peruvian customization begins */}
                    <div className='col-12 ms-header__row'>
                        <div className="row ms-header__topbar__row">
                            <div className='row ms-header__icons__row'>
                                {props.preferredStore}
                                <Node {...Divider} />
                                <div className='order-status-header-link'>
                                    <a href={props.config.orderStatusLink}>{props.config.orderStatusLabel}</a>
                                </div>
                                <Node {...Divider} />
                                {_renderAccountBlock(props, false)}
                                <Node {...Divider} />
                                <div className='ms-header__phone'>
                                    {props.config.companyPhone}
                                </div>
                            </div>
                        </div>
                        <div className="row ms-header__bottombar__row">
                            <Node className={'col-4 ms-header__search__col-4'}>
                                {props.navIcon}
                                {_renderReactFragment(props.search)}
                            </Node>
                            <div className='col-4 ms-header__logo__col-4'>
                                {props.logo}
                            </div>
                            <div className="col-4 ms-header__cart__col-4">
                                {props.cartIcon}
                            </div>
                        </div>
                    </div>
                    {/* peruvian customization ends */}
                </Node>
                {_renderCollapseMenu(props)}
                <Node className={'ms-header__desktop-view'}>
                    {_renderReactFragment(props.menuBar)}
                </Node>
            </Node>
        </Module>
    );
};

function _renderCollapseMenu(props: IHeaderViewProps & IHeaderProps<{}>): JSX.Element | null {
    const { MobileMenuLinksContainer, mobileMenuCollapsed } = props;
    return (
        <Collapse className={'ms-header__collapsible-hamburger'} isOpen={!mobileMenuCollapsed}>
            {_renderReactFragment(props.menuBar)}
            <Node {...MobileMenuLinksContainer}>
                {props.preferredStore}
                <div className='order-status-header-link'>
                    <a href={props.config.orderStatusLink}>{props.config.orderStatusLabel}</a>
                </div>
                {_renderMobileAccountBlock(props, true)}
                {props.siteOptions}
                <div className='ms-header__phone'>
                    {props.config.companyPhone}
                </div>
            </Node>
        </Collapse>
    );
}

const renderCustomerName = (accountInformation: AsyncResult<Customer>): React.ReactChild => {
    const customer = accountInformation && accountInformation.result;
    return (
        <>{customer ? customer.FirstName || customer.Name : ''}</>
    );
};

function _renderMobileAccountBlock(props: IHeaderViewProps, renderForMobile: boolean): JSX.Element | null {
    const {
        AccountInfoDropdownParentContainer,
        signOutLink,
        signInLink,
        data,
        accountLinks } = props;

    if (AccountInfoDropdownParentContainer) {
        const accountClassName = classnames('ms-header__drawer', AccountInfoDropdownParentContainer.className, renderForMobile ? 'account-mobile' : 'account-desktop');

        if (accountLinks) {
            return (
                <Drawer className={accountClassName} openGlyph='ms-header__drawer-open' closeGlyph='ms-header__drawer-close' glyphPlacement='end' toggleButtonText={renderCustomerName(data.accountInformation)}>
                    <div>
                        {accountLinks ? accountLinks.map((link: React.ReactNode) => link) : false}
                        {signOutLink}
                    </div>
                </Drawer>
            );
        } else if (signInLink) {
            return (
                <Node {...AccountInfoDropdownParentContainer} className={accountClassName}>
                    {signInLink}
                </Node>
            );
        }
    }
    return null;
}

function _renderAccountBlock(props: IHeaderViewProps, renderForMobile: boolean): JSX.Element | null {
    const {
        AccountInfoDropdownParentContainer,
        AccountInfoDropdownPopoverConentContainer,
        accountInfoDropdownButton,
        signOutLink,
        signInLink,
        accountLinks,
    } = props;

    if (AccountInfoDropdownParentContainer) {
        const accountClassName = classnames(AccountInfoDropdownParentContainer.className, renderForMobile ? 'account-mobile' : 'account-desktop');
        if (AccountInfoDropdownPopoverConentContainer) {
            return (
                <Node {...AccountInfoDropdownParentContainer} className={accountClassName}>
                    {accountInfoDropdownButton}
                    <Node {...AccountInfoDropdownPopoverConentContainer}>
                        {accountLinks ? accountLinks.map((link: React.ReactNode) => link) : false}
                        {signOutLink}
                    </Node>
                </Node>
            );
        } else if (signInLink) {
            return (
                <Node {...AccountInfoDropdownParentContainer} className={accountClassName}>
                    {signInLink}
                </Node>
            );
        }
    }

    return null;
}

function _renderReactFragment(items: React.ReactNode[]): JSX.Element | null {
    return (
        <React.Fragment>
            {items && items.length
                ? items.map((slot: React.ReactNode, index: number) => {
                    return <React.Fragment key={index}>{slot}</React.Fragment>;
                })
                : null}
        </React.Fragment>
    );
}

export default headerView;